import type {RequirementOf} from '../../lib/types';
import {resolveFillMode} from '../../lib/fill-mode';
import {useControlFrame} from './control-frame';
import {SlotMarks} from '../SlotMarks';
import {type ElemProps} from './element-props';
import {buildBaseStyle, optionRowStyle} from './element-styles';

/**
 * The two controls that offer a fixed set of answers.
 *
 * They answer the option-typography question identically, so they answer it in
 * one place — `optionRowStyle` — and share a file so a change to one is made
 * next to the other rather than remembered about it.
 */

export function CheckboxRenderer({
  item,
  fillMode,
  fillValue,
  onFillChange,
  fillReadOnly,
  textStyle,
}: ElemProps & {item: RequirementOf<'checkbox'>}) {
  const s = item.style;
  const style = buildBaseStyle(s);
  // Each option row carries the element's text style, default included, so the
  // size the toolbar shows is the size that renders (DERP-226: adjustable
  // options), with a cell's own type over the top — see `optionRowStyle`.
  const optionStyle = optionRowStyle(s, textStyle);
  const cfg = item.config;
  const opts = cfg.options ?? [];
  const defaultChecked = cfg.default_checked ?? [];
  const locked = cfg.lock_value ?? false;
  const mode = resolveFillMode({
    fillMode,
    hasOnChange: !!onFillChange,
    fillReadOnly: !!fillReadOnly,
  });
  const controlled = mode === 'controlled' || mode === 'readonly';
  const selected =
    controlled && Array.isArray(fillValue) ? (fillValue as string[]) : defaultChecked;
  const toggle = (opt: string) =>
    onFillChange?.(selected.includes(opt) ? selected.filter((o) => o !== opt) : [...selected, opt]);
  const hideLabels = cfg.hide_labels ?? false;
  return (
    <div style={{...style, width: cfg.width}} className="relative">
      <SlotMarks required={item.required} config={cfg} />
      <div className="flex flex-col gap-1" style={optionBlockStyle(s.text_align)}>
        {opts.map((opt) => {
          const isPinned = fillMode && locked && defaultChecked.includes(opt);
          return (
            <label
              key={opt}
              title={hideLabels ? opt : undefined}
              style={optionStyle}
              className={`flex items-center gap-2 ${isPinned ? 'cursor-not-allowed' : fillMode ? 'cursor-pointer' : 'cursor-default'}`}
            >
              {controlled ? (
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  disabled={isPinned || !!fillReadOnly}
                  onChange={() => (isPinned || fillReadOnly ? undefined : toggle(opt))}
                  className="h-3 w-3"
                />
              ) : fillMode ? (
                isPinned ? (
                  <input type="checkbox" checked onChange={() => {}} disabled className="h-3 w-3" />
                ) : (
                  <input
                    type="checkbox"
                    defaultChecked={defaultChecked.includes(opt)}
                    className="h-3 w-3"
                  />
                )
              ) : (
                <input
                  type="checkbox"
                  checked={defaultChecked.includes(opt)}
                  onChange={() => {}}
                  className="h-3 w-3"
                />
              )}
              {/* Hidden labels stay in the DOM so the box keeps its accessible name. */}
              <span className={hideLabels ? 'sr-only' : isPinned ? 'text-gray-400' : undefined}>
                {opt}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Where the options sit across the element's width.
 *
 * The options move as one block, so the boxes stay lined up in a column
 * whatever length each option's text is. The rows are flex, so the wrapper's
 * `text-align` never reached them; the block is placed by its margins instead.
 * Unset keeps the rows full width, as they always were.
 */
function optionBlockStyle(align: string | undefined): React.CSSProperties | undefined {
  if (align === 'center') return {width: 'fit-content', marginLeft: 'auto', marginRight: 'auto'};
  if (align === 'right') return {width: 'fit-content', marginLeft: 'auto'};
  return undefined;
}

export function DropdownRenderer({
  item,
  fillMode,
  fillValue,
  onFillChange,
  fillReadOnly,
  textStyle,
}: ElemProps & {item: RequirementOf<'dropdown'>}) {
  const frame = useControlFrame();
  const s = item.style;
  const style = buildBaseStyle(s);
  const optionStyle = optionRowStyle(s, textStyle);
  const cfg = item.config;
  const opts = cfg.options ?? [];
  const defaultVal = cfg.default_value ?? '';
  const mode = resolveFillMode({
    fillMode,
    hasOnChange: !!onFillChange,
    fillReadOnly: !!fillReadOnly,
  });
  const locked = (cfg.lock_value ?? false) || mode === 'readonly';
  const hasFill = (mode === 'controlled' || mode === 'readonly') && typeof fillValue === 'string';
  const fv = hasFill ? (fillValue as string) : defaultVal;
  const displayLabel = (mode === 'readonly' ? fv : defaultVal) || 'Select…';
  /**
   * Answerable, or a value to be read — the only two things this box can be.
   *
   * It was four branches: not in fill mode, locked, controlled, uncontrolled.
   * The first two draw the SAME static row (a locked one adds a cursor and
   * nothing else) and the last two are one `<select>` differing by how it is
   * bound — the shape `bind` already collapses in `TextRenderer`. Read-only and
   * locked dropdowns draw a label rather than a disabled `<select>`, which is a
   * deliberate presentation choice for this element and the reason the split is
   * here rather than inside the control.
   */
  const asStatic = !fillMode || locked;
  const bind =
    mode === 'controlled'
      ? {
          value: fv,
          onChange: (e: React.ChangeEvent<HTMLSelectElement>) => onFillChange?.(e.target.value),
        }
      : {defaultValue: defaultVal};
  return (
    <div style={{...style, width: cfg.width}} className="relative">
      <SlotMarks required={item.required} config={cfg} />
      {asStatic ? (
        <div
          style={optionStyle}
          className={`flex items-center justify-between rounded px-2 py-1 ${frame('closed')} ${defaultVal ? 'text-gray-700' : 'text-gray-400'} ${fillMode ? 'cursor-not-allowed' : ''}`}
        >
          <span>{displayLabel}</span>
          <span>▾</span>
        </div>
      ) : (
        <select style={optionStyle} {...bind} className={`w-full rounded p-1 ${frame('select')}`}>
          <option value="">Select…</option>
          {opts.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
